import React, { memo, useCallback, useMemo, useState } from 'react'

import { FormOnChange, Select } from '@procsea/design-system'

import { TagFormModal, TagFormValue } from 'src/components/presentationals/shared/forms'
import { TagType } from 'src/types'
import { findById } from 'src/utils'

import { useTours } from './useTours'

interface SelectedTag {
  id: Id
  name: string
}

export interface TourSelectorProps {
  'data-e2e'?: string
  canManageTours?: boolean
  disabled?: boolean
  errorMessage?: string
  hasMultiMemberships?: boolean
  label?: string
  name?: string
  onChange?: FormOnChange<Id | null>
  value?: Id | null
}

const TourSelector = ({
  'data-e2e': dataE2e = 'tour-select',
  canManageTours,
  disabled,
  errorMessage,
  hasMultiMemberships,
  label = gettext('Default tour'),
  name,
  onChange,
  value,
}: TourSelectorProps) => {
  const { tourOptions, isLoading, onSubmit, onArchive } = useTours({ hasMultiMemberships })
  const [isTourFormModalOpen, setIsTourFormModalOpen] = useState(false)
  const [selectedTag, setSelectedTag] = useState<SelectedTag>()

  const tags: SelectedTag[] = useMemo(
    () => tourOptions.map(tourOption => ({ name: tourOption.label, id: tourOption.value })),
    [JSON.stringify(tourOptions)]
  )

  const openModal = useCallback(() => {
    setIsTourFormModalOpen(true)
  }, [setIsTourFormModalOpen])

  const closeModal = useCallback(() => {
    setIsTourFormModalOpen(false)
    setSelectedTag(undefined)
  }, [setIsTourFormModalOpen, setSelectedTag])

  const handleCreate = useCallback(() => {
    openModal()
  }, [openModal])

  const handleEdit = useCallback(
    (id: Id) => {
      const tag = findById(tags)(id)

      setSelectedTag(tag)
      openModal()
    },
    [openModal, JSON.stringify(tags)]
  )

  const handleArchive = useCallback(
    (id?: Id) => {
      if (id) {
        onArchive(id).then(() => {
          closeModal()

          if (value === id) {
            onChange?.({ value: null })
          }
        })
      }
    },
    [value, onArchive, closeModal, onChange]
  )

  const handleSubmit = useCallback(
    (form: TagFormValue) => {
      onSubmit(form).then(id => {
        onChange?.({ value: id })
        closeModal()
      })
    },
    [onChange, closeModal]
  )

  if (!canManageTours && !tourOptions.length) return null

  return (
    <>
      <Select<Id, false>
        clearable
        createLabel={gettext('Create a tour')}
        data-e2e={dataE2e}
        disabled={disabled}
        editLabel={gettext('edit')}
        errorMessage={errorMessage}
        fluid
        isLoading={isLoading}
        label={label}
        name={name}
        onChange={onChange}
        onCreate={canManageTours ? handleCreate : undefined}
        onEdit={canManageTours ? handleEdit : undefined}
        options={tourOptions}
        placeholder={canManageTours ? gettext('Select or create a tour') : gettext('Select a tour')}
        value={value}
      />

      {isTourFormModalOpen && (
        <TagFormModal
          isEditDisabled={!!selectedTag}
          isLoading={isLoading}
          isTagFormModalOpen={isTourFormModalOpen}
          onArchive={handleArchive}
          onClose={closeModal}
          onSubmit={handleSubmit}
          selectedTag={selectedTag}
          tags={tags}
          type={TagType.TOUR}
        />
      )}
    </>
  )
}

export default memo(TourSelector)
